/**
 * Utilitário de refresh de token agnóstico
 * Centraliza o refresh independente do provider (Azure/Keycloak)
 */

import logger from '@/shared/lib/logger';

type TokenRefreshFunction = () => Promise<string | null>;
type TokenRefreshFailureHandler = (reason: string) => void | Promise<void>;

let refreshFunction: TokenRefreshFunction | null = null;
let refreshInFlightPromise: Promise<string | null> | null = null;
let refreshFailureHandler: TokenRefreshFailureHandler | null = null;
let refreshFailureHandled = false;

/** Registra a função de refresh do provider ativo */
export const registerTokenRefresh = (fn: TokenRefreshFunction): void => {
  refreshFunction = fn;
  logger.debug('[TokenRefresh] Função de refresh registrada');
};

/** Tenta renovar o token usando o provider registrado */
export const refreshAccessToken = async (): Promise<string | null> => {
  if (!refreshFunction) {
    logger.warn('[TokenRefresh] Nenhuma função de refresh registrada');
    return null;
  }

  if (refreshInFlightPromise) {
    logger.debug('[TokenRefresh] Reutilizando refresh em andamento');
    return refreshInFlightPromise;
  }

  try {
    logger.debug('[TokenRefresh] Tentando renovar token...');
    refreshInFlightPromise = refreshFunction();

    const newToken = await refreshInFlightPromise;

    if (newToken) {
      refreshFailureHandled = false;
      logger.info('[TokenRefresh] Token renovado com sucesso', {
        tokenLength: newToken.length,
      });
      return newToken;
    }

    logger.warn('[TokenRefresh] Refresh retornou null');
    return null;
  } catch (error) {
    logger.error('[TokenRefresh] Falha ao renovar token', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  } finally {
    refreshInFlightPromise = null;
  }
};

/** Registra handler para falhas definitivas de refresh (ex.: 401 após retry) */
export const registerTokenRefreshFailureHandler = (handler: TokenRefreshFailureHandler): void => {
  refreshFailureHandler = handler;
  logger.debug('[TokenRefresh] Handler de falha de refresh registrado');
};

/** Dispara reautenticação quando o refresh falha de forma definitiva */
export const handleTokenRefreshFailure = async (reason: string): Promise<void> => {
  if (!refreshFailureHandler) {
    logger.warn('[TokenRefresh] Nenhum handler de falha de refresh registrado', { reason });
    return;
  }

  if (refreshFailureHandled) {
    logger.debug('[TokenRefresh] Falha já tratada anteriormente, ignorando', { reason });
    return;
  }

  refreshFailureHandled = true;
  try {
    await refreshFailureHandler(reason);
  } catch (error) {
    logger.error('[TokenRefresh] Erro ao executar handler de falha de refresh', {
      reason,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

/** Limpa handler de falha de refresh */
export const clearTokenRefreshFailureHandler = (): void => {
  refreshFailureHandler = null;
  refreshFailureHandled = false;
  logger.debug('[TokenRefresh] Handler de falha de refresh limpo');
};

/** Limpa a função de refresh registrada (chamado no logout) */
export const clearTokenRefresh = (): void => {
  refreshFunction = null;
  refreshInFlightPromise = null;
  refreshFailureHandled = false;
  logger.debug('[TokenRefresh] Função de refresh limpa');
};
